import React from 'react';
import Icon from '../../common/components/Icon';
import { useTranslations } from '../../localization/useTranslations';
import Button from '../../components/buttons/SimpleButton/Button';
import ActivationPremiumBenefits from './ActivationPremiumBenefits';
import { FinishPageProps } from '../types/CodeInfo';

const FinishPage: React.FC<FinishPageProps & { updateSnackbarMessage: (message: string) => void }> = ({
  errorStatus,
  subscriptionType,
  featureCodes,
  updateSnackbarMessage,
}) => {
  const translations = useTranslations();

  // Extracting error and success descriptions from window
  const errorDescription = window.forbes_subscription_activation?.errorDescription;
  const digitalSuccessDescription = window.forbes_subscription_activation?.digitalSuccessDescription;
  const printSuccessDescription = window.forbes_subscription_activation?.printSuccessDescription;

  const pageType = errorStatus ? 'error' : 'success';

  if (pageType === 'error') {
    updateSnackbarMessage(translations?.snackBarMessages?.failedActivation || 'Failed Activation');
  }

  return (
    <div className="page-subscription-activation__last-step">
      {pageType === 'error' ? (
        // Display error content
        <>
          <div className="page-subscription-activation__icon">
            <Icon name="error" size="xlarge" />
          </div>

          <h2 className="page-subscription-activation__title heading heading--archivo heading--800">
            {translations?.subscriptionActivation?.failedActivationTitle || 'Failed Activation'}
          </h2>

          {errorDescription && (
            <div className="page-subscription-activation__description">
              <div dangerouslySetInnerHTML={{ __html: errorDescription }} />
            </div>
          )}

          <Button
            className="button--secondary button--large button--full-width button--icon"
            href="/"
            label={translations?.salesFunnel?.footerButtonText || 'Go to Homepage'}
            iconName="f"
          />
        </>
      ) : (
        // Display success content based on subscriptionType
        <>
          <div className="page-subscription-activation__icon_new">
            <svg
              width="446"
              height="140"
              viewBox="0 0 446 140"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ maxWidth: '100%' }}
            >
              <g clip-path="url(#clip0_6571_117461)">
                <rect width="445" height="140" transform="translate(0.5)" fill="white" />
                <mask
                  id="mask0_6571_117461"
                  style={{ maskType: 'alpha' }}
                  maskUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="826"
                  height="140"
                >
                  <rect x="0.5" width="825" height="140" fill="white" />
                </mask>
                <g mask="url(#mask0_6571_117461)">
                  <circle cx="223" cy="69.5" r="384" fill="white" stroke="#E2E8F0" />
                  <circle cx="223" cy="69.5" r="354" fill="white" stroke="#E2E8F0" />
                  <circle cx="223" cy="69.5" r="324" fill="white" stroke="#E2E8F0" />
                  <circle cx="223" cy="69.5" r="294" fill="white" stroke="#E2E8F0" />
                  <circle cx="223" cy="69.5" r="264" fill="white" stroke="#E2E8F0" />
                  <circle cx="223" cy="69.5" r="234" fill="white" stroke="#E2E8F0" />
                  <circle cx="223" cy="69.5" r="204" fill="white" stroke="#E2E8F0" />
                  <circle cx="223" cy="69.5" r="174" fill="white" stroke="#E2E8F0" />
                  <circle cx="223" cy="69.5" r="144" fill="white" stroke="#E2E8F0" />
                  <circle cx="223.5" cy="70" r="113.5" fill="white" stroke="#E2E8F0" />
                  <circle cx="223.5" cy="70" r="83.5" fill="white" stroke="#E2E8F0" />
                  <circle cx="223.5" cy="70" r="53.5" fill="white" stroke="#E2E8F0" />
                  <rect
                    x="0.5"
                    width="445"
                    height="140"
                    fill="url(#paint0_radial_6571_117461)"
                    style={{ mixBlendMode: 'overlay' }}
                  />
                  <circle cx="111.5" cy="70" r="19.5" fill="white" stroke="#E2E8F0" />
                  <path
                    d="M108.501 69V68.8632C108.501 67.5056 108.435 65.9924 109.204 65.0208C109.643 64.4667 110.353 64 111.5 64C112.647 64 113.357 64.4667 113.795 65.0208C114.565 65.9924 114.499 67.5056 114.499 68.8632V69M106.5 69.8235C106.5 69.3687 106.5 69 106.5 69H116.5V74.3529C116.5 75.2626 116.5 76 116.5 76H106.5C106.5 76 106.5 75.2626 106.5 74.3529V69.8235Z"
                    stroke="#475569"
                    stroke-width="1.5"
                    stroke-linecap="square"
                  />
                  <circle cx="335.5" cy="70" r="19.5" fill="white" stroke="#E2E8F0" />
                  <path
                    d="M329.5 69.375V65H333.7L335.8 66.875H341.5V69.375M329.5 69.375V75H341.5V69.375M329.5 69.375H341.5"
                    stroke="#475569"
                    stroke-width="1.5"
                    stroke-linecap="square"
                  />
                  <g filter="url(#filter0_dd_6571_117461)">
                    <circle cx="223.5" cy="70" r="32" fill="#047857" />
                  </g>
                </g>
                <path
                  d="M234.929 63.3333L220.643 78.3333L213.5 70.833"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="square"
                />
              </g>
              <defs>
                <filter
                  id="filter0_dd_6571_117461"
                  x="171.5"
                  y="38"
                  width="104"
                  height="104"
                  filterUnits="userSpaceOnUse"
                  color-interpolation-filters="sRGB"
                >
                  <feFlood flood-opacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feMorphology radius="5" operator="erode" in="SourceAlpha" result="effect1_dropShadow_6571_117461" />
                  <feOffset dy="20" />
                  <feGaussianBlur stdDeviation="12.5" />
                  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_6571_117461" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feMorphology radius="5" operator="erode" in="SourceAlpha" result="effect2_dropShadow_6571_117461" />
                  <feOffset dy="10" />
                  <feGaussianBlur stdDeviation="5" />
                  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.04 0" />
                  <feBlend mode="normal" in2="effect1_dropShadow_6571_117461" result="effect2_dropShadow_6571_117461" />
                  <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_6571_117461" result="shape" />
                </filter>
                <radialGradient
                  id="paint0_radial_6571_117461"
                  cx="0"
                  cy="0"
                  r="1"
                  gradientUnits="userSpaceOnUse"
                  gradientTransform="translate(223 70) scale(198.767 70)"
                >
                  <stop stop-color="#020617" />
                  <stop offset="1" stop-color="white" />
                </radialGradient>
                <clipPath id="clip0_6571_117461">
                  <rect width="445" height="140" fill="white" transform="translate(0.5)" />
                </clipPath>
              </defs>
            </svg>
          </div>

          <h2 className="page-subscription-activation__title" style={{ marginTop: '16px' }}>
            {translations?.subscriptionActivation?.activatedTitle || 'Activated'}
          </h2>

          {(subscriptionType === 'print' || subscriptionType === 'both') && printSuccessDescription && (
            // Display Print Subscription content
            <div className="page-subscription-activation__description">
              <div
                dangerouslySetInnerHTML={{
                  __html: printSuccessDescription,
                }}
              />
            </div>
          )}

          {(subscriptionType === 'web' || subscriptionType === 'both') && digitalSuccessDescription && (
            // Display Digital Subscription content
            <div className="page-subscription-activation__description">
              <div
                dangerouslySetInnerHTML={{
                  __html: digitalSuccessDescription,
                }}
              />
            </div>
          )}

          {featureCodes && featureCodes.length > 0 && <ActivationPremiumBenefits featureCodes={featureCodes} />}

          <Button
            className="button--icon"
            type="secondary"
            size="large"
            fullWidth={true}
            href="/"
            label={translations?.salesFunnel?.footerButtonText || 'Go to Homepage'}
            iconName="f"
          />
        </>
      )}
    </div>
  );
};

export default FinishPage;
